import { Link, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { BookingSection } from "@/components/BookingSection";
import { SEO_PAGES, SEO_NAV } from "@/lib/seo";
import { PHONES } from "@/lib/site";

const Shell = ({ children, testid }) => (
  <div className="App min-h-screen" data-testid={testid}>
    <Navbar />
    <main className="pt-[72px]">{children}</main>
    <Footer />
    <WhatsAppButton />
  </div>
);

export default function SeoPage() {
  const { pathname } = useLocation();
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/our-work") return <Navigate to="/our-services" replace />;
  const page = SEO_PAGES[path];
  if (!page) return <NotFound />;

  return (
    <Shell testid={`seo-page-${path.slice(1)}`}>
      <section className="mx-auto max-w-5xl px-5 py-20 lg:px-8 lg:py-28">
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="font-mono text-xs uppercase tracking-[0.3em] text-amber-500">
          {page.eyebrow}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mt-4 font-display text-4xl uppercase leading-[0.95] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl"
          data-testid="seo-page-h1"
        >
          {page.h1}
        </motion.h1>
        {page.body.map((para, i) => (
          <motion.p key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.05 }} className="mt-8 max-w-3xl text-base leading-relaxed text-slate-300 sm:text-lg" data-testid="seo-page-body">
            {para}
          </motion.p>
        ))}
        <div className="mt-10 flex flex-wrap gap-4">
          {!page.showBooking && (
            <Link to="/contact" data-testid="seo-page-book-cta" className="group flex items-center gap-3 bg-amber-600 px-7 py-4 font-mono text-sm uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-500">
              Book online <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          )}
          {PHONES.map((p) => (
            <a key={p.id} href={p.tel} data-testid={`seo-page-call-${p.id}`} className="flex items-center gap-3 border border-white/20 px-6 py-4 font-mono text-sm tracking-wider text-slate-200 transition-colors hover:border-amber-600 hover:text-amber-500">
              <Phone size={15} /> {p.display}
            </a>
          ))}
        </div>
        <nav className="mt-16 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/10 pt-8" aria-label="More pages" data-testid="seo-page-links">
          {SEO_NAV.filter((l) => l.to !== path).map((l) => (
            <Link key={l.to} to={l.to} className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 transition-colors hover:text-amber-500">{l.label}</Link>
          ))}
          <Link to="/" className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 transition-colors hover:text-amber-500">Home</Link>
        </nav>
      </section>
      {page.showBooking && <BookingSection />}
    </Shell>
  );
}

export function NotFound() {
  return (
    <Shell testid="not-found-page">
      <section className="mx-auto max-w-3xl px-5 py-32 text-center lg:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-500">404</p>
        <h1 className="mt-4 font-display text-4xl uppercase tracking-tight text-slate-50 sm:text-5xl" data-testid="not-found-h1">Page not found</h1>
        <p className="mt-6 text-base text-slate-400">That address doesn't exist on our site. Head back to the homepage or give us a call.</p>
        <Link to="/" data-testid="not-found-home-link" className="mt-10 inline-flex items-center gap-3 bg-amber-600 px-7 py-4 font-mono text-sm uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-500">
          <ArrowLeft size={16} /> Back to homepage
        </Link>
      </section>
    </Shell>
  );
}
